/** 大屏案件面板权限码（与 sys_resource.CODE 一致） */
export const LARGE_SCREEN_PERM = {
  regionCaseStatus: 'largeScreenRegion01',
  regionCountryRank: 'largeScreenRegion02',
  regionCityRank: 'largeScreenRegion03',
  orgCaseStatus: 'largeScreenOrg01',
  orgCountryRank: 'largeScreenOrg02',
  orgCityRank: 'largeScreenOrg03',
} as const

export type LargeScreenCaseQueryMode = 'region' | 'org' | 'none'

export type CasePanelFlags = {
  caseStatus: boolean
  countryRank: boolean
  cityRank: boolean
}

const REGION_PERM_CODES = [
  LARGE_SCREEN_PERM.regionCaseStatus,
  LARGE_SCREEN_PERM.regionCountryRank,
  LARGE_SCREEN_PERM.regionCityRank,
] as const

const ORG_PERM_CODES = [
  LARGE_SCREEN_PERM.orgCaseStatus,
  LARGE_SCREEN_PERM.orgCountryRank,
  LARGE_SCREEN_PERM.orgCityRank,
] as const

const PANEL_PERM: Record<
  Exclude<LargeScreenCaseQueryMode, 'none'>,
  { caseStatus: string; countryRank: string; cityRank: string }
> = {
  region: {
    caseStatus: LARGE_SCREEN_PERM.regionCaseStatus,
    countryRank: LARGE_SCREEN_PERM.regionCountryRank,
    cityRank: LARGE_SCREEN_PERM.regionCityRank,
  },
  org: {
    caseStatus: LARGE_SCREEN_PERM.orgCaseStatus,
    countryRank: LARGE_SCREEN_PERM.orgCountryRank,
    cityRank: LARGE_SCREEN_PERM.orgCityRank,
  },
}

const DISABLED_PANELS: CasePanelFlags = {
  caseStatus: false,
  countryRank: false,
  cityRank: false,
}

export function hasLargeScreenRegionPerm(hasPerm: (code: string) => boolean): boolean {
  return REGION_PERM_CODES.some((code) => hasPerm(code))
}

export function hasLargeScreenOrgPerm(hasPerm: (code: string) => boolean): boolean {
  return ORG_PERM_CODES.some((code) => hasPerm(code))
}

/**
 * 有地区权限码 → 地区数据；有组织权限码 → 组织数据。
 * 路由明确指定时优先路由；两者都有且走默认入口时优先地区。
 */
export function resolveLargeScreenCaseQueryMode(
  path: string,
  hasRegionPerm: boolean,
  hasOrgPerm: boolean,
): LargeScreenCaseQueryMode {
  if (path.includes('/dashboard-screen/org')) {
    return hasOrgPerm ? 'org' : 'none'
  }
  if (path.includes('/dashboard-screen/region')) {
    return hasRegionPerm ? 'region' : 'none'
  }
  if (hasRegionPerm) return 'region'
  if (hasOrgPerm) return 'org'
  return 'none'
}

export function resolveDashboardScreenRouteName(
  hasOrgPerm: boolean,
  hasRegionPerm: boolean,
): string {
  if (hasOrgPerm && !hasRegionPerm) return 'dashboard-screen-org'
  if (hasRegionPerm && !hasOrgPerm) return 'dashboard-screen-region'
  return 'dashboard-screen'
}

/** 当前模式下，各案件子面板是否可请求 */
export function resolveCasePanelFlags(
  mode: LargeScreenCaseQueryMode,
  hasPerm: (code: string) => boolean,
): CasePanelFlags {
  if (mode === 'none') return DISABLED_PANELS
  const codes = PANEL_PERM[mode]
  return {
    caseStatus: hasPerm(codes.caseStatus),
    countryRank: hasPerm(codes.countryRank),
    cityRank: hasPerm(codes.cityRank),
  }
}
